import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Mobiliário que resiste ao ambiente úmido e ao uso intenso da academia',
    description: 'MDF revestido em laminado HPL resistente a suor, umidade e produtos de limpeza. Estruturas em aço com pintura eletrostática anticorrosiva. Projetado para aguentar o ritmo de uma academia movimentada.',
  },
  {
    icon: 'ri-layout-grid-line',
    title: 'Recepção, vestiários e sala de avaliação completos',
    description: 'Do balcão de recepção que impressiona o aluno na entrada aos armários dos vestiários e à mesa de avaliação física — projetamos todos os ambientes da academia com funcionalidade e visual premium.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito de toda a academia',
    description: 'Visualize a recepção, vestiários, sala de avaliação e área de convivência em 3D antes de fabricar. Aprovação completa do layout e acabamentos — sem surpresas.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem fora do horário de pico',
    description: 'Nossa equipe instala nos horários de menor movimento da academia — madrugada, manhã cedo ou fins de semana. Sem interromper os treinos dos alunos.',
  },
];

const blocks = [
  {
    title: 'Recepção que Converte Visitantes em Alunos Matriculados',
    text: [
      'O aluno em potencial entra na academia e em segundos decide se vai matricular ou não. A recepção é o ponto de conversão — e ela precisa transmitir profissionalismo, organização e energia. Um balcão bem projetado, cadeiras confortáveis para espera e uma identidade visual forte fazem a diferença entre o visitante que sai sem matricular e o que fecha o plano anual.',
      'Nossos balcões de recepção para academias incluem tampo em granito ou laminado HPL resistente a riscos e umidade, frente em MDF com acabamento que pode receber o logo da academia em relevo ou adesivo, espaço interno organizado para computador, impressora e documentos, cadeiras de espera em courvin resistente a suor e fácil limpeza, e painel de fundo com iluminação LED que valoriza a identidade visual da academia.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20gym%20fitness%20center%20reception%20desk%20with%20sleek%20counter%2C%20LED%20lighting%2C%20energetic%20design%2C%20dark%20tones%20with%20accent%20colors%2C%20professional%20gym%20interior%2C%20welcoming%20atmosphere%2C%20high-end%20fitness%20club%20lobby&width=800&height=500&seq=academia_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Vestiários Organizados que Elevam a Experiência do Aluno',
    text: [
      'O vestiário é o ambiente mais usado da academia depois da área de treino. Armários que não fecham, bancos que rangem e bancadas que descascam com a umidade são reclamações frequentes que aparecem nas avaliações do Google. Um vestiário bem equipado é um diferencial competitivo real.',
      'Projetamos armários para vestiários em MDF revestido em laminado HPL resistente à umidade, com fechadura de chave ou cadeado, ventilação lateral e acabamento que não descasca com o vapor do banho. Bancos em MDF com estrutura em aço inox ou alumínio que não enferruja. Bancadas de apoio com tampo em granito ou laminado HPL. Espelhos de corpo inteiro com moldura em alumínio. Tudo projetado para o ambiente úmido e o uso intenso do vestiário de academia.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20gym%20locker%20room%20with%20organized%20lockers%2C%20wooden%20benches%2C%20clean%20design%2C%20good%20lighting%2C%20premium%20fitness%20center%20changing%20room%2C%20neutral%20tones%2C%20professional%20interior%20photography&width=800&height=500&seq=academia_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Sala de Avaliação Física e Área de Convivência Profissionais',
    text: [
      'A sala de avaliação física é onde o personal trainer ou o professor de educação física faz a anamnese, mede o percentual de gordura e define o treino do aluno. Ela precisa transmitir competência técnica e conforto para que o aluno se sinta seguro para compartilhar seus objetivos e limitações.',
      'Para a sala de avaliação, oferecemos mesa de trabalho com tampo em granito ou laminado HPL, cadeiras ergonômicas para o professor e o aluno, armário para fichas e equipamentos de avaliação, e maca estofada em courvin resistente a suor. Para a área de convivência, mesas e cadeiras resistentes, frigobar embutido e painel de TV com suporte articulado. Ambientes que completam a experiência premium da academia.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Professional%20gym%20assessment%20room%20with%20desk%2C%20ergonomic%20chairs%2C%20organized%20cabinets%2C%20clean%20modern%20design%2C%20fitness%20evaluation%20space%2C%20neutral%20tones%2C%20professional%20interior%20photography&width=800&height=500&seq=academia_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20gym%20reception%20counter%20with%20LED%20lighting%2C%20dark%20tones%2C%20energetic%20design%2C%20professional%20fitness%20club%20lobby%2C%20high-end%20interior&width=600&height=400&seq=academia_g1&orientation=landscape',
    alt: 'Recepção de academia com balcão e iluminação LED',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Gym%20locker%20room%20with%20organized%20wooden%20lockers%2C%20metal%20benches%2C%20clean%20modern%20design%2C%20premium%20fitness%20center%20changing%20room&width=600&height=400&seq=academia_g2&orientation=landscape',
    alt: 'Vestiário de academia com armários organizados',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Fitness%20center%20waiting%20area%20with%20comfortable%20chairs%2C%20modern%20design%2C%20gym%20branding%2C%20energetic%20atmosphere%2C%20professional%20interior&width=600&height=400&seq=academia_g3&orientation=landscape',
    alt: 'Área de espera de academia com cadeiras confortáveis',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Professional%20gym%20assessment%20evaluation%20room%20with%20desk%2C%20chairs%2C%20organized%20space%2C%20clean%20modern%20fitness%20interior%2C%20neutral%20tones&width=600&height=400&seq=academia_g4&orientation=landscape',
    alt: 'Sala de avaliação física profissional',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Academia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function AcademiaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Academia e Fitness Caruaru PE | Home Office Premium',
    description: 'Mobiliário para academias e centros fitness em Caruaru PE. Recepção, vestiários, sala de avaliação e área de convivência. Materiais resistentes à umidade, projeto 3D gratuito e montagem fora do horário de pico. Atendemos academias no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário academia Recife, móveis fitness Petrolina, armário vestiário academia Garanhuns, recepção academia Caruaru, mobiliário gym Agreste PE, armário academia Pernambuco, vestiário academia Nordeste, balcão recepção academia Recife, armário vestiário Petrolina, mobiliário academia Caruaru',
    path: '/academia',
  });

  useSchemaLD({
    id: 'schema-academia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Academias e Centros Fitness | Caruaru PE',
      description: 'Mobiliário para academias: recepção, vestiários, sala de avaliação e área de convivência. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/academia`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Academias e Centros Fitness',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20premium%20gym%20fitness%20center%20interior%20with%20sleek%20reception%20area%2C%20dark%20tones%20with%20warm%20accent%20lighting%2C%20energetic%20professional%20atmosphere%2C%20high-end%20fitness%20club%20design%2C%20wide%20angle%20photography&width=1400&height=700&seq=academia_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Academias · Centros Fitness · Crossfit
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que resiste ao ritmo da sua academia e impressiona seus alunos.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Recepção que converte, vestiários organizados e sala de avaliação profissional. Materiais resistentes à umidade e ao uso intenso — porque academia não para.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem fora do horário de pico · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que academias de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Academia</span> Projetados para Performance
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja sua academia completa em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Recepção, vestiários e sala de avaliação projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem fora do horário de pico — sem interromper os treinos.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes de <span className="text-[#E8610A]">Academia Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
